import { useEffect, useRef, useState } from 'react';
import { Controls } from './components/Controls';
import { Preview } from './components/Preview';
import { buildFileName, cloneDefaultConfig, validateConfig } from './config';
import { downloadJson, exportPng } from './exporter';
import { deleteProject, listProjects, loadDraft, saveDraft, saveProject } from './storage';
import type { GeneratorConfig, StoredProject } from './types';
import { validateExportFileName, validatePngFile } from './validation';

interface Notice {
  kind: 'success' | 'error' | 'info';
  message: string;
}

export default function App() {
  const [config, setConfig] = useState<GeneratorConfig>(cloneDefaultConfig);
  const [projects, setProjects] = useState<StoredProject[]>([]);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const initialFileName = buildFileName(config);
  const [fileName, setFileName] = useState(initialFileName);
  const previousDefaultFileName = useRef(initialFileName);
  const jsonInputRef = useRef<HTMLInputElement>(null);
  const fileNameError = validateExportFileName(fileName);

  useEffect(() => {
    void Promise.all([loadDraft(), listProjects()])
      .then(([draft, storedProjects]) => {
        if (draft) setConfig(validateConfig(draft));
        setProjects(storedProjects);
      })
      .catch(() => setNotice({ kind: 'error', message: 'Browser storage is unavailable.' }))
      .finally(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!ready) return;
    const timer = window.setTimeout(() => {
      void saveDraft(config).catch(() =>
        setNotice({ kind: 'error', message: 'Could not auto-save the current settings.' }),
      );
    }, 300);
    return () => window.clearTimeout(timer);
  }, [config, ready]);

  useEffect(() => {
    let nextDefault: string;
    try {
      nextDefault = buildFileName(config);
    } catch {
      return;
    }
    if (fileName === previousDefaultFileName.current) setFileName(nextDefault);
    previousDefaultFileName.current = nextDefault;
  }, [config, fileName]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 4200);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const refreshProjects = async () => setProjects(await listProjects());

  const handleSaveProject = async () => {
    const id = currentProjectId || crypto.randomUUID();
    const project: StoredProject = {
      id,
      name: config.title.trim() || 'Untitled screen',
      updatedAt: new Date().toISOString(),
      config: structuredClone(config),
    };
    try {
      await saveProject(project);
      setCurrentProjectId(id);
      await refreshProjects();
      setNotice({ kind: 'success', message: 'Project saved in this browser.' });
    } catch {
      setNotice({ kind: 'error', message: 'Could not save this project.' });
    }
  };

  const handleNewProject = () => {
    setConfig(cloneDefaultConfig());
    setCurrentProjectId(null);
    setNotice({ kind: 'info', message: 'New unsaved project created.' });
  };

  const handleDuplicateProject = async () => {
    try {
      const copyName = `${config.title.trim() || 'Untitled screen'} copy`;
      const project: StoredProject = {
        id: crypto.randomUUID(),
        name: copyName,
        updatedAt: new Date().toISOString(),
        config: { ...structuredClone(config), title: copyName },
      };
      await saveProject(project);
      setConfig(project.config);
      setCurrentProjectId(project.id);
      await refreshProjects();
      setNotice({ kind: 'success', message: 'Project duplicated.' });
    } catch {
      setNotice({ kind: 'error', message: 'Could not duplicate this project.' });
    }
  };

  const handleDeleteProject = async () => {
    if (!currentProjectId) return;
    const project = projects.find((item) => item.id === currentProjectId);
    if (!window.confirm(`Delete “${project?.name || 'this project'}” from this browser?`)) return;
    try {
      await deleteProject(currentProjectId);
      setCurrentProjectId(null);
      await refreshProjects();
      setNotice({ kind: 'success', message: 'Project deleted.' });
    } catch {
      setNotice({ kind: 'error', message: 'Could not delete this project.' });
    }
  };

  const handleProjectSelect = (id: string) => {
    if (!id) {
      setCurrentProjectId(null);
      return;
    }
    const selected = projects.find((project) => project.id === id);
    if (!selected) return;
    try {
      setConfig(validateConfig(selected.config));
      setCurrentProjectId(selected.id);
      setNotice({ kind: 'info', message: `Loaded “${selected.name}”.` });
    } catch {
      setNotice({ kind: 'error', message: 'This saved project is invalid.' });
    }
  };

  const handleLogoUpload = async (file: File) => {
    const validationError = await validatePngFile(file);
    if (validationError) {
      setNotice({ kind: 'error', message: validationError });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setConfig((current) => ({
        ...current,
        logo: { ...current.logo, dataUrl: String(reader.result), fileName: file.name },
      }));
    };
    reader.onerror = () => setNotice({ kind: 'error', message: 'Could not read the logo file.' });
    reader.readAsDataURL(file);
  };

  const handleJsonImport = (file: File) => {
    if (file.size > 50 * 1024 * 1024) {
      setNotice({ kind: 'error', message: 'The JSON configuration must be 50 MB or smaller.' });
      return;
    }
    if (
      file.type &&
      file.type !== 'application/json' &&
      !file.name.toLowerCase().endsWith('.json')
    ) {
      setNotice({ kind: 'error', message: 'Select a JSON configuration file.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const imported = validateConfig(JSON.parse(String(reader.result)));
        setConfig(imported);
        setCurrentProjectId(null);
        setNotice({ kind: 'success', message: 'JSON configuration imported.' });
      } catch (error) {
        setNotice({
          kind: 'error',
          message: error instanceof Error ? error.message : 'Invalid JSON configuration.',
        });
      }
    };
    reader.onerror = () => setNotice({ kind: 'error', message: 'Could not read the JSON file.' });
    reader.readAsText(file);
  };

  const handlePngExport = async () => {
    if (fileNameError) {
      setNotice({ kind: 'error', message: fileNameError });
      return;
    }
    setExporting(true);
    setNotice({ kind: 'info', message: 'Rendering full-resolution PNG…' });
    try {
      await exportPng(config, fileName);
      setNotice({ kind: 'success', message: 'PNG export completed.' });
    } catch (error) {
      setNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'PNG export failed.',
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            SG
          </span>
          <span>
            <strong>ScreenGrid Generator</strong>
            <small>by MKL Labs</small>
          </span>
        </div>
        <div className="project-actions">
          <label className="project-select">
            <span className="sr-only">Saved project</span>
            <select
              value={currentProjectId || ''}
              onChange={(event) => handleProjectSelect(event.target.value)}
            >
              <option value="">Unsaved project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="button button--ghost" onClick={handleNewProject}>
            New
          </button>
          <button type="button" className="button button--ghost" onClick={handleSaveProject}>
            Save
          </button>
          <details className="more-menu">
            <summary className="button button--ghost" aria-label="More project actions">
              •••
            </summary>
            <div className="menu-popover">
              <button type="button" onClick={handleDuplicateProject}>
                Duplicate project
              </button>
              <button type="button" onClick={() => downloadJson(config)}>
                Export configuration JSON
              </button>
              <button type="button" onClick={() => jsonInputRef.current?.click()}>
                Import configuration JSON
              </button>
              <button
                type="button"
                className="danger-text"
                disabled={!currentProjectId}
                onClick={handleDeleteProject}
              >
                Delete saved project
              </button>
            </div>
          </details>
          <input
            ref={jsonInputRef}
            className="sr-only"
            type="file"
            accept="application/json,.json"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) handleJsonImport(file);
              event.target.value = '';
            }}
          />
        </div>
      </header>

      <main className="workspace">
        <aside className="settings-panel">
          <Controls config={config} updateConfig={setConfig} onLogoUpload={handleLogoUpload} />
        </aside>
        <div className="preview-panel">
          <Preview config={config} updateConfig={setConfig} />
          <div className="export-bar">
            <label className="export-name">
              <span>Export file</span>
              <input
                value={fileName}
                maxLength={180}
                spellCheck={false}
                aria-invalid={Boolean(fileNameError)}
                aria-describedby={fileNameError ? 'export-file-error' : undefined}
                onChange={(event) => setFileName(event.target.value)}
              />
              {fileNameError && (
                <small className="field-error" id="export-file-error">
                  {fileNameError}
                </small>
              )}
            </label>
            <button
              type="button"
              className="button button--primary export-button"
              disabled={exporting || Boolean(fileNameError)}
              onClick={handlePngExport}
            >
              {exporting ? 'Rendering…' : 'Export PNG'}
            </button>
          </div>
        </div>
      </main>

      <footer className="app-footer">
        <div className="footer-info">
          <span className="footer-copyright">
            ScreenGrid Generator © 2026{' '}
            <a href="https://mkl-labs.io" target="_blank" rel="noreferrer">
              MKL Labs
            </a>
            .
          </span>
          <span>
            Generator privacy: projects, settings, and logos stay in your browser and are not
            uploaded. No accounts, analytics, or tracking. Hosting may process your IP address and
            access logs.
          </span>
        </div>
        <nav className="footer-links" aria-label="Legal and developer links">
          <a href="https://mkl-labs.io/imprint">Imprint</a>
          <a href="https://mkl-labs.io/privacy">Privacy</a>
          <a href="/licenses.txt">Licenses</a>
        </nav>
      </footer>

      {notice && (
        <div className={`notice notice--${notice.kind}`} role="status">
          {notice.message}
        </div>
      )}
    </div>
  );
}
